import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { Button } from "@/components/ui/button";
import { FNM_ESTATE_OFFER } from "@/lib/agency-estate-offer";
import { FIRM } from "@/lib/firm";
import { GOOGLE_REVIEWS, GOOGLE_REVIEW_URL } from "@/lib/google-reviews";
import coastalImage from "@/assets/home-hero-coast.jpg";

const offer = FNM_ESTATE_OFFER;
const erinReviews = GOOGLE_REVIEWS.filter((review) => ["kimberley-hall", "mark-northfield", "emma-lea"].includes(review.id));
const formattedPrice = `$${offer.price.toLocaleString("en-AU")}`;
const description = `Exclusive First National Moreton family estate-planning pack: 2 wills and 2 enduring powers of attorney for ${formattedPrice} including GST at Coastside Law.`;

export const Route = createFileRoute("/first-national-moreton-estate-planning")({
  head: () => ({
    meta: [
      { title: "First National Moreton family estate-planning offer | Coastside Law" },
      { name: "description", content: description },
      { property: "og:title", content: "First National Moreton family pack | Coastside Law" },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EstateOfferPage,
});

function EstateOfferPage() {
  return <>
    <PageHero eyebrow={`Exclusive to ${offer.agency} clients`} title="Family estate-planning pack" lead="A property move is a good time to review who inherits your assets and who can make decisions for you. Take care of your wills and enduring powers of attorney with Coastside Law." image={{ src: coastalImage, alt: "Sunshine Coast coastline" }} />
    <section className="mx-auto max-w-3xl px-5 py-12">
      <p className="font-serif text-5xl text-foreground">${offer.price.toLocaleString("en-AU")}</p>
      <p className="mt-2 font-semibold text-primary">Including GST · {offer.agency} family special</p>
      <ul className="mt-6 space-y-3 border-y border-border py-6 text-lg text-foreground">
        <li>{offer.wills} wills — record your wishes for your estate</li>
        <li>{offer.epoas} enduring powers of attorney — appoint people to make decisions if needed</li>
      </ul>
      <p className="mt-5 leading-relaxed text-muted-foreground">Tell us about your family and circumstances so we can confirm the documents are right for you and explain any additional requirements before work begins. Mention First National Moreton when you enquire.</p>
      <Button asChild className="mt-6"><a href="#enquiry">Enquire about the family pack</a></Button>
      <h2 className="mt-14 font-serif text-3xl text-foreground">What clients say about Erin</h2>
      <p className="mt-3 text-sm text-muted-foreground">Google reviews from property clients, shared here for their experience working with Erin.</p>
      <div className="mt-6 space-y-6">
        {erinReviews.map((review) => <blockquote key={review.id} className="border-l-2 border-primary pl-5">
          <p className="text-primary" aria-label="5 out of 5 stars">★★★★★</p>
          <p className="mt-2 leading-relaxed text-muted-foreground">“{review.body}”</p>
          <footer className="mt-3 text-sm font-semibold text-foreground">{review.name} · Google review</footer>
        </blockquote>)}
      </div>
      <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-block text-sm font-semibold text-primary underline">View Google reviews</a>
      <div className="mt-14"><EnquiryForm heading={`Enquire about the ${formattedPrice} family pack`} defaultMatterType="wills-estates" defaultMessage={`I am a First National Moreton client interested in the ${formattedPrice} family pack (2 wills and 2 enduring powers of attorney, including GST).`} /></div>
      <p className="mt-6 text-muted-foreground">Prefer to speak with Erin? <a href={FIRM.phoneHref} className="font-semibold text-primary underline">{FIRM.phone}</a></p>
    </section>
  </>;
}